import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { showModal } from '../../../../redux/modules/modal';
import { useInsuredActiveRecordsData, useInsuredAllRecordsData, useInsuredArchivedRecordsData, useInsuredData } from '../../../../services/insured-service';
import { infiniteListDataDoesExist } from '../../../../services/util';
import ArchivedRecordsModal from '../Records/RecordsHeader/ArchivedRecordsModal';

import RecordDetails from './components/RecordDetails';

const RecordDetailsContainer = ({ companyId, insuredId, recordId }) => {
  const dispatch = useDispatch();
  const { data: insured } = useInsuredData(insuredId);
  const { data: insuredAllRecords, isLoading: allRecordsLoading } = useInsuredAllRecordsData({ insuredId, options: { retry: false } });
  const { data: insuredActiveRecords, isLoading: activeRecordsLoading } = useInsuredActiveRecordsData({ insuredId, options: { retry: false } });
  const { data: insuredArchivedRecords, isLoading: archivedRecordsLoading } = useInsuredArchivedRecordsData({ insuredId, options: { retry: false } });

  const insuredIsArchived = useMemo(() => !!insured?.archivedAt, [insured]);
  const insuredIsDeleted = useMemo(() => !!insured?.deletedAt, [insured]);

  const insuredIsActiveButOnlyArchivedRecords = useMemo(() => {
    if (archivedRecordsLoading || activeRecordsLoading) {
      return false;
    }

    return infiniteListDataDoesExist(insuredArchivedRecords) && !infiniteListDataDoesExist(insuredActiveRecords);
  }, [insuredActiveRecords, insuredArchivedRecords]);

  const insuredIsActiveButNoRecords = useMemo(() => {
    if (allRecordsLoading) {
      return false;
    }

    return !infiniteListDataDoesExist(insuredAllRecords);
  }, [insuredAllRecords]);

  if (!allRecordsLoading && insuredIsActiveButNoRecords) {
    return (
      <Box>
        <Text mb={4} textStyle="bodyLg500">No Records Exist for the Insured.</Text>
        <Text textStyle="bodyMd">
          Visit the Projects Page and select a project to create a record for this insured.
        </Text>
      </Box>
    );
  }

  if (!recordId || !insuredId || !companyId || insuredIsArchived || insuredIsDeleted || activeRecordsLoading) {
    return (
      <Box height="200px" />
    );
  }

  if (insuredIsActiveButOnlyArchivedRecords) {
    return (
      <Box>
        <Text mb={4} textStyle="bodyLg500">No Active Records Exist for the Insured.</Text>
        <Button
          minWidth="300px"
          size="lg"
          onClick={() => {
            dispatch(showModal(ArchivedRecordsModal, {
              title: 'Archived Records',
              maxWidth: 1040
            }, { insuredId }));
          }}
        >
          View Archived Records
        </Button>
      </Box>
    );
  }

  return (
    <RecordDetails
      companyId={companyId}
      insuredId={insuredId}
      recordId={recordId}
    />
  );
};

RecordDetailsContainer.propTypes = {
  companyId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired
};

export default RecordDetailsContainer;
